// JS mirror of SCSS design tokens in src/assets/scss/design-tokens/.
// Keep this file in sync with the *.scss files in that folder.
// Components should consume these tokens via the MUI theme (theme.palette / theme.spacing / theme.tokens)
// instead of hard-coding values inside `sx` or inline `style` props.

export const colors = {
  primary: {
    0: '#9CF1FA',
    100: '#44DFED',
    200: '#2EC5D2',
    300: '#24A0AB',
    400: '#1E8B94',
    500: '#1A8089',
    600: '#12636A',
    700: '#0B4A50',
    800: '#08393D',
    900: '#072426',
  },
  success: {
    0: '#9BF97B',
    100: '#6AE446',
    200: '#56CA35',
    300: '#45A429',
    400: '#3A8E22',
    500: '#35831F',
    600: '#286616',
    700: '#1C4C0E',
    800: '#163A0C',
    900: '#13240A',
  },
  error: {
    0: '#F8DCD7',
    100: '#F5BCB3',
    200: '#F4998B',
    300: '#F05F53',
    400: '#E53838',
    500: '#D43333',
    600: '#A72626',
    700: '#7F1A1A',
    800: '#621413',
    900: '#3C100A',
  },
  warning: {
    0: '#FBDFA0',
    100: '#F2C432',
    200: '#D6AC20',
    300: '#AE8B18',
    400: '#977813',
    500: '#8B6F11',
    600: '#6D560B',
    700: '#524006',
    800: '#3E3007',
    900: '#271E07',
  },
  information: {
    0: '#DAE3F6',
    100: '#B8CCF2',
    200: '#92B4EF',
    300: '#5294E7',
    400: '#4381CC',
    500: '#3D76BC',
    600: '#2E5C94',
    700: '#214570',
    800: '#193456',
    900: '#102137',
  },
  black: {
    0: '#E1E2E8',
    100: '#C7CBD5',
    200: '#ACB2C1',
    300: '#8791AB',
    400: '#737D97',
    500: '#69748C',
    600: '#51596c',
    700: '#3E434E',
    800: '#2F333C',
    900: '#182030',
    1: '#000000',
    2: '#8a8a8a',
    3: '#4e4e4e',
    4: '#2e2e2e',
    5: '#797878',
  },
  gray: {
    1: '#ffffff',
    2: '#eeeeee',
    3: '#f8f8f8',
    4: '#bbbbbb',
    5: '#bcbcbc',
    6: '#888888',
    7: '#6f6f6f',
    8: '#555555',
    9: '#3c3c3c',
    10: '#222222',
    11: '#D9D9D9',
  },
  white: '#ffffff',
  brand: '#2B366E',
};

export const semanticColors = {
  background: colors.gray[1],
  surface: colors.white,
  onPrimary: colors.white,
  onSurface: colors.black[1],
  onBackground: colors.black[1],
  disabled: colors.gray[4],
  divider: colors.gray[4],
  focus: '#1976d2',
  hoverOverlay: 'rgba(44, 62, 80, 0.04)',
  activeOverlay: 'rgba(44, 62, 80, 0.08)',
  backgroundSelected: '#E8F4F4',
  boxShadow: '#0000001A',
  borderDefault: colors.black[0],
  toolbarBoxShadow: '#00000012',
  queryBuilderInfoIcon: '#727c8f',
};

export const spacing = {
  0: 0,
  2: '0.125rem',
  4: '0.25rem',
  6: '0.375rem',
  8: '0.5rem',
  10: '0.625rem',
  12: '0.75rem',
  13: '0.8125rem',
  16: '1rem',
  18: '1.125rem',
  20: '1.25rem',
  24: '1.5rem',
  28: '1.75rem',
  32: '2rem',
  40: '2.5rem',
  48: '3rem',
  56: '3.5rem',
  64: '4rem',
  80: '5rem',
  96: '6rem',
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '2rem',
  xl: '4rem',
};

export const typography = {
  fontFamily: {
    primary: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
    secondary: "'Georgia', 'Times New Roman', serif",
  },
  fontSize: {
    10: '0.625rem',
    12: '0.75rem',
    14: '0.875rem',
    16: '1rem',
    18: '1.125rem',
    20: '1.25rem',
    24: '1.5rem',
    28: '1.75rem',
    32: '2rem',
    40: '2.5rem',
    48: '3rem',
  },
  lineHeight: {
    extraSmall: 1,
    small: 1.25,
    medium: 1.5,
    large: 1.75,
    heading: 1.2,
  },
  fontWeight: {
    thin: 100,
    extraLight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
    black: 900,
  },
};

export const breakpoints = {
  xs: 320,
  sm: 544,
  md: 768,
  lg: 1012,
  xl: 1440,
};

export const radius = {
  xs: '0.125rem',
  sm: '0.25rem',
  md: '0.5rem',
  lg: '1rem',
  xl: '2rem',
};

export const shadows = {
  modal: 24,
  card: '0 2px 8px #0000001A',
  toolbar: '0 1px 4px #00000012',
};

export const zIndex = {
  mobileStepper: 1000,
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

const tokens = {
  colors,
  semanticColors,
  spacing,
  typography,
  breakpoints,
  radius,
  shadows,
  zIndex,
};

export default tokens;
